import Image from 'next/image'
import Link from 'next/link'

import {
  appStoreBadge,
  arrowForward,
  atmLogoWhite,
  atomThumbsUp,
  decorativeLines,
  facebookIcon,
  footerBgPattern,
  globeIcon,
  googlePlayBadge,
  instagramIcon,
  linkedinIcon,
  profile2,
  profile3,
  profile4,
  xIcon,
} from '@ui/assets'
import { CookiePreferencesLink } from '@ui/components/consent'
import { appUrl, marketingUrl } from '@ui/lib/urls'

interface FooterProps {
  chinColor?: 'white' | 'light-blue'
}

export function Footer({ chinColor = 'light-blue' }: FooterProps) {
  const chinBgColor = chinColor === 'white' ? 'bg-white' : 'bg-[#e6f7ff]'
  return (
    <footer className="relative bg-[#0a2d4a]">
      {/* Background pattern - covers both sections */}
      <div className="absolute inset-0 z-0">
        <Image src={footerBgPattern} fill alt="" className="object-cover" />
      </div>

      {/* Fast Cash Zero Hassle Section */}
      <section className="relative">
        {/* Rounded top border */}
        <div
          className={`absolute left-0 right-0 top-0 z-[1] h-[49px] rounded-bl-[60px] rounded-br-[60px] ${chinBgColor}`}
        />

        {/* Content */}
        <div className="relative z-10 px-4 pb-12 pt-32 text-center sm:px-6 lg:px-8 lg:pb-24">
          {/* Main heading with globe icon */}
          <div className="relative mb-16">
            <h2 className="font-display text-5xl leading-[0.95] tracking-[-2.75px] text-white md:text-7xl lg:text-[110px]">
              Fast{' '}
              <span className="relative text-[#29bfed]">
                <span className="absolute -top-4 left-1/2 inline-block h-[60px] w-[60px] -translate-x-1/2 rotate-45 lg:-top-8 lg:h-[104px] lg:w-[100px]">
                  <Image
                    src={globeIcon}
                    width={100}
                    height={104}
                    alt=""
                    className="h-full w-full"
                  />
                </span>
                Cash
              </span>
              <br />
              <span className="relative inline-flex items-center -space-x-4">
                Zero
                <span className="relative inline-block h-[80px] w-[80px] lg:h-[150px] lg:w-[150px]">
                  <Image
                    src={atomThumbsUp}
                    width={150}
                    height={150}
                    alt=""
                    className="h-full w-full object-contain"
                  />
                </span>
                Hassle
              </span>
            </h2>
          </div>

          {/* CTA buttons */}
          <div className="hidden items-center justify-center gap-7 lg:flex">
            <a
              href={marketingUrl('/download')}
              className="inline-flex items-center gap-3 rounded-full bg-white py-1 pl-6 pr-1 transition-all hover:bg-gray-50"
            >
              <span className="text-[17px] font-bold tracking-[-0.34px] text-[#1e1e1e]">
                Download App
              </span>
              <div className="flex h-[48px] w-[48px] items-center justify-center rounded-full bg-[#29bfed]">
                <Image src={arrowForward} width={24} height={24} alt="" />
              </div>
            </a>

            <div className="relative flex h-[43px] items-center gap-4">
              <div className="flex items-center pr-2">
                <div className="relative -mr-2 h-[42px] w-[42px]">
                  <Image
                    src={profile3}
                    width={46}
                    height={46}
                    alt=""
                    className="rounded-full ring-2 ring-white"
                  />
                </div>
                <div className="relative -mr-2 h-[42px] w-[42px]">
                  <Image
                    src={profile4}
                    width={46}
                    height={46}
                    alt=""
                    className="rounded-full ring-2 ring-white"
                  />
                </div>
                <div className="relative h-[42px] w-[42px]">
                  <Image
                    src={profile2}
                    width={46}
                    height={46}
                    alt=""
                    className="rounded-full ring-2 ring-white"
                  />
                </div>
              </div>
              <div className="font-display text-left text-[16.5px] uppercase leading-[1.1] tracking-[-0.33px] text-white">
                Loved by
                <br />
                1.5M+ Users
              </div>
              {/* Decorative accent */}
              <div className="absolute -right-4 -top-4 rotate-[150.676deg] scale-y-[-100%]">
                <Image src={decorativeLines} width={32} height={24} alt="" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <section className="relative px-4 pb-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-[30px] border border-white/10 bg-[rgba(7,33,54,0.7)] px-12 pb-6 pt-12 backdrop-blur-[15px]">
            {/* Top section with links */}
            <div className="mb-12 flex flex-col items-start gap-8 lg:flex-row lg:justify-between">
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4 lg:gap-[118px]">
                {/* Logo and Social */}
                <div className="flex flex-col gap-6 lg:justify-between lg:gap-0">
                  <div className="w-[95px]">
                    <Image
                      src={atmLogoWhite}
                      alt="ATM.com"
                      className="h-auto w-full"
                    />
                  </div>
                  {/* Social icons */}
                  <div className="flex gap-2">
                    <a
                      href="https://www.linkedin.com/company/atmdotcom"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex aspect-square h-[46px] min-h-[46px] w-[46px] min-w-[46px] items-center justify-center rounded-[8px] bg-white/10 transition-colors hover:bg-white/20"
                    >
                      <Image
                        src={linkedinIcon}
                        width={20}
                        height={20}
                        alt="LinkedIn"
                      />
                    </a>
                    <a
                      href="https://www.facebook.com/atmdotcom#"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex aspect-square h-[46px] min-h-[46px] w-[46px] min-w-[46px] items-center justify-center rounded-[8px] bg-white/10 transition-colors hover:bg-white/20"
                    >
                      <Image
                        src={facebookIcon}
                        width={20}
                        height={20}
                        alt="Facebook"
                      />
                    </a>
                    <a
                      href="https://x.com/atmdotcom"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex aspect-square h-[46px] min-h-[46px] w-[46px] min-w-[46px] items-center justify-center rounded-[8px] bg-white/10 transition-colors hover:bg-white/20"
                    >
                      <Image src={xIcon} width={20} height={20} alt="X" />
                    </a>
                    <a
                      href="https://www.instagram.com/atmapp"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex aspect-square h-[46px] min-h-[46px] w-[46px] min-w-[46px] items-center justify-center rounded-[8px] bg-white/10 transition-colors hover:bg-white/20"
                    >
                      <Image
                        src={instagramIcon}
                        width={20}
                        height={20}
                        alt="Instagram"
                      />
                    </a>
                  </div>
                </div>

                {/* Features column */}
                <div className="flex flex-col gap-3 text-[14px] tracking-[-0.28px] text-white/80">
                  <Link
                    href={marketingUrl('/about')}
                    className="hover:text-white"
                  >
                    About
                  </Link>
                  <Link
                    href={marketingUrl('/compare/')}
                    className="hover:text-white"
                  >
                    Comparisons
                  </Link>
                  <Link
                    href={marketingUrl('/guides')}
                    className="hover:text-white"
                  >
                    Guides
                  </Link>
                  <Link
                    href={marketingUrl('/privacy-policy')}
                    className="hover:text-white"
                  >
                    Privacy Policy
                  </Link>
                </div>

                {/* Company column */}
                <div className="flex flex-col gap-3 text-[14px] tracking-[-0.28px] text-white/80">
                  <Link
                    href={marketingUrl('/best-budgeting-apps')}
                    className="hover:text-white"
                  >
                    Budgeting
                  </Link>
                  <Link
                    href={marketingUrl('/careers')}
                    className="hover:text-white"
                  >
                    Careers
                  </Link>
                  <Link
                    href={marketingUrl('/streakstakes')}
                    className="hover:text-white"
                  >
                    Streakstakes Rules
                  </Link>
                  <Link
                    href={marketingUrl('/terms-of-service')}
                    className="hover:text-white"
                  >
                    Terms of Service
                  </Link>
                </div>

                {/* Legal column */}
                <div className="flex flex-col gap-3 text-[14px] tracking-[-0.28px] text-white/80">
                  <Link
                    href={marketingUrl('/press')}
                    className="hover:text-white"
                  >
                    Press
                  </Link>
                  <Link
                    href={marketingUrl('/best-cash-advance-apps')}
                    className="hover:text-white"
                  >
                    Cash Advance
                  </Link>
                  <a
                    href={appUrl('/support')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white"
                  >
                    Get Help
                  </a>
                  <Link
                    href={marketingUrl('/legal-disclaimers')}
                    className="hover:text-white"
                  >
                    Legal/Disclaimers
                  </Link>
                  <CookiePreferencesLink className="text-left hover:text-white" />
                </div>
              </div>

              {/* App store badges */}
              <div className="flex flex-col gap-3">
                <a
                  href="https://apps.apple.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Image
                    src={appStoreBadge}
                    width={120}
                    height={40}
                    alt="Download on App Store"
                  />
                </a>
                <a
                  href="https://play.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Image
                    src={googlePlayBadge}
                    width={135}
                    height={40}
                    alt="Get it on Google Play"
                  />
                </a>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="border-t border-white/10 pt-6">
              <p className="text-[14px] leading-[1.2] text-white/40">
                © {new Date().getFullYear()} ATM.com, Inc. All Rights Reserved.
                ATM.com® may receive affiliate compensation for purchases made
                from affiliate sellers when you click on their links on our
                site. The opinions expressed are solely those of the author and
                have not been influenced or endorsed by any financial
                institution or credit card issuer. While we aim to provide
                accurate and current information, we cannot guarantee its
                completeness or accuracy. You are responsible for verifying the
                information and making your own financial decisions. ATM.com
                does not offer credit cards or make credit decisions.
              </p>
            </div>
          </div>
        </div>
      </section>
    </footer>
  )
}
